import { useEffect, useMemo, useRef, useState } from 'react';
import zhConvertor from 'zhconvertor';
import CandidateIcons from '../SeatDetail/CandidateIcons';
import { getBgColorByParty } from '../SeatDetail/seatHelpers';
import './VoteUpdates.css';

const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

const reduceMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Vote tally that eases from its previous value to the new one when the
// live poll bumps it, instead of jumping.
const CountUpNumber = ({ value }) => {
  const [shown, setShown] = useState(value);
  const shownRef = useRef(value);
  shownRef.current = shown;

  useEffect(() => {
    const from = shownRef.current;
    if (from === value || reduceMotion()) {
      setShown(value);
      return undefined;
    }
    const duration = 600;
    let raf;
    let start;
    const tick = (now) => {
      if (start === undefined) start = now;
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(from + (value - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return <strong>{shown.toLocaleString()}</strong>;
};

// Latest vote updates (最新战况): seats ordered by their votes' last update
// time, each row showing the currently leading candidate. Renders nothing
// until at least one seat has an update, and disappears once every seat's
// result is official. `data` is a seatinfo API array; `onSeatClick(seatname)`
// is fired when a row is clicked. Hosts reset the paging by remounting (key).
const VoteUpdates = ({ data, onSeatClick }) => {
  // How many lines are visible; "view more" adds 10.
  const [updatesShown, setUpdatesShown] = useState(5);

  // Seats with at least one vote update and a leader with actual votes
  // (a 0-vote "leader" is noise), newest first.
  const voteUpdates = useMemo(
    () =>
      (data || [])
        .filter(
          (s) =>
            Number(s.last_upd) > 0 &&
            (s.candidate || []).some((c) => Number(c.votes) > 0)
        )
        .sort((a, b) => Number(b.last_upd) - Number(a.last_upd)),
    [data]
  );
  const allOfficial =
    (data || []).length > 0 && data.every((s) => Number(s.is_o) === 1);

  // Seats whose leader tally changed since the previous poll get a brief
  // background flash. The first snapshot doesn't flash anything.
  const prevVotesRef = useRef(null);
  const [flashSet, setFlashSet] = useState(() => new Set());
  useEffect(() => {
    const next = new Map();
    voteUpdates.forEach((seat) => {
      const top = [...(seat.candidate || [])].sort(
        (a, b) => (b.votes || 0) - (a.votes || 0)
      )[0];
      if (top) next.set(seat.seatname, Number(top.votes || 0));
    });
    const prev = prevVotesRef.current;
    prevVotesRef.current = next;
    if (!prev) return undefined;
    const changed = new Set();
    next.forEach((votes, seatname) => {
      if (prev.has(seatname) && prev.get(seatname) !== votes) {
        changed.add(seatname);
      }
    });
    if (changed.size === 0) return undefined;
    setFlashSet(changed);
    const timer = setTimeout(() => setFlashSet(new Set()), 1400);
    return () => clearTimeout(timer);
  }, [voteUpdates]);

  //if (allOfficial || voteUpdates.length === 0) return null;
  if (voteUpdates.length === 0) return null;

  return (
    <div className="vote_updates">
      <h3>
        <span className="vu-live-dot" />
        {tr('最新战况')}
      </h3>
      <ul className="vote_updates__list">
        {voteUpdates.slice(0, updatesShown).map((seat) => {
          const ranked = [...(seat.candidate || [])].sort(
            (a, b) => (b.votes || 0) - (a.votes || 0)
          );
          const leader = ranked[0];
          if (!leader) return null;
          const isOfficial = Number(seat.is_o) === 1;
          // Official seats show the majority (lead over the runner-up);
          // uncontested or single-candidate seats fall back to the tally.
          const majority = ranked[1]
            ? Number(leader.votes || 0) - Number(ranked[1].votes || 0)
            : Number(leader.votes || 0);
          return (
            <li key={seat.seatname}>
              <button
                type="button"
                className={`vote_updates__row ${
                  flashSet.has(seat.seatname) ? 'is-updated' : ''
                }`}
                style={{ '--accent': getBgColorByParty(leader) }}
                onClick={() => onSeatClick(seat.seatname)}
              >
                <span className="vu-seat">
                  {seat.seatname} {tr(seat.ednamec)}
                </span>
                <span className="vu-icon">
                  <CandidateIcons candidate={leader} />
                </span>
                <span className="vu-party">
                  {leader.party} {leader.alliance && `(${leader.alliance})`}
                </span>
                <span className="vu-photo">
                  {leader.candidate_logo && (
                    <img
                      className="cand-photo"
                      src={leader.candidate_logo}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </span>
                <span className="vu-name">{tr(leader.candidate_name)}</span>
                <span className="vu-votes">
                  {tr('以')}{' '}
                  <CountUpNumber
                    value={isOfficial ? majority : Number(leader.votes || 0)}
                  />
                  {isOfficial ? tr('张多数票 中选') : tr('张票 暂时领先')}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {updatesShown < voteUpdates.length && (
        <div className="vote_updates__more">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setUpdatesShown((n) => n + 10)}
          >
            {tr('查看更多')}
          </button>
        </div>
      )}
    </div>
  );
};

export default VoteUpdates;
